import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui-kit/select';
import { OptionAction, QuestionOption, Section } from '../../types/form-engine.types';

const ACTIONS: { value: OptionAction; label: string }[] = [
  { value: 'continue', label: 'CONTINUE' },
  { value: 'continue_and_end', label: 'CONTINUE_AND_END' },
  { value: 'end', label: 'END_FORM' },
  { value: 'jump_to_section', label: 'JUMP_TO_SECTION' },
];

export interface OptionActionConfigProps {
  option: QuestionOption;
  sections: Section[];
  currentSectionId?: string;
  onChange: (option: QuestionOption) => void;
}

export const OptionActionConfig = ({ option, sections, currentSectionId, onChange }: OptionActionConfigProps) => {
  const { t } = useTranslation();
  const targets = sections.filter((section) => section.SectionId !== currentSectionId);
  const action = option.Action ?? 'continue';

  const handleActionChange = (next: OptionAction) => {
    if (next === 'jump_to_section' && targets.length === 0) return;
    onChange({ ...option, Action: next, TargetSectionId: next === 'jump_to_section' ? option.TargetSectionId : undefined });
  };

  return (
    <div className="order-last flex w-full flex-wrap items-center gap-2 sm:order-none sm:w-auto">
      <div className="w-full sm:w-44">
        <Select value={action} onValueChange={(value: OptionAction) => handleActionChange(value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTIONS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {t(item.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {action === 'jump_to_section' && (
        <div className="w-full sm:w-52">
          {targets.length === 0 ? (
            <p className="text-xs text-error">{t('NO_SECTIONS_AVAILABLE_FOR_JUMP')}</p>
          ) : (
            <Select
              value={option.TargetSectionId ?? ''}
              onValueChange={(value) => onChange({ ...option, TargetSectionId: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('SELECT_SECTION')} />
              </SelectTrigger>
              <SelectContent>
                {targets.map((section) => (
                  <SelectItem key={section.SectionId} value={section.SectionId}>
                    {section.Title || `${t('SECTION')} ${section.Position + 1}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {targets.length > 0 && !option.TargetSectionId && (
            <p className="mt-1 text-xs text-error">{t('SECTION_SELECTION_REQUIRED')}</p>
          )}
        </div>
      )}
    </div>
  );
};
